import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { CIMTeaserAnalysis, CIMTeaserExportFormat, CIMTeaserJob } from '../types/cimTeaser';

export function useCIMTeaserJob(projectId?: string, jobId?: string) {
  const [job, setJob] = useState<CIMTeaserJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !jobId) {
      setJob(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      doc(db, 'projects', projectId, 'cimTeaserJobs', jobId),
      (snapshot) => {
        setJob(snapshot.exists() ? snapshot.data() as CIMTeaserJob : null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [projectId, jobId]);

  return { job, loading, error };
}

export function useCIMTeaserAnalysis(projectId?: string, teaserId?: string) {
  const [analysis, setAnalysis] = useState<CIMTeaserAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !teaserId) {
      setAnalysis(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      doc(db, 'projects', projectId, 'cimTeaserAnalyses', teaserId),
      (snapshot) => {
        setAnalysis(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } as CIMTeaserAnalysis : null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [projectId, teaserId]);

  return { analysis, loading, error };
}

export function useCIMTeaserActions() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const createTeaserProject = useCallback(async (primaryFileName: string, documentCount: number) => {
    if (!user) throw new Error('User must be authenticated');
    const createProject = httpsCallable<
      { primaryFileName: string; documentCount: number },
      { success: boolean; projectId: string }
    >(functions, 'createCIMTeaserProject', { timeout: 120_000 });

    const result = await createProject({ primaryFileName, documentCount });
    return result.data.projectId;
  }, [user]);

  const startTeaserAnalysis = useCallback(async (projectId: string, documentIds: string[]) => {
    if (!user) throw new Error('User must be authenticated');
    const startAnalysis = httpsCallable<
      { projectId: string; documentIds: string[] },
      { success: boolean; projectId: string; jobId: string }
    >(functions, 'startCIMTeaserAnalysis', { timeout: 120_000 });

    const result = await startAnalysis({ projectId, documentIds });
    return result.data;
  }, [user]);

  const exportTeaserAnalysis = useCallback(async (
    projectId: string,
    teaserId: string,
    format: CIMTeaserExportFormat
  ) => {
    if (!user) throw new Error('User must be authenticated');
    setBusy(true);
    try {
      const exportAnalysis = httpsCallable<
        { projectId: string; teaserId: string; format: CIMTeaserExportFormat },
        { success: boolean; downloadUrl: string; fileName: string; contentType: string }
      >(functions, 'exportCIMTeaserAnalysis', { timeout: 180_000 });

      const result = await exportAnalysis({ projectId, teaserId, format });
      return result.data;
    } finally {
      setBusy(false);
    }
  }, [user]);

  return {
    busy,
    createTeaserProject,
    startTeaserAnalysis,
    exportTeaserAnalysis,
  };
}
